import { useMutation, useQuery } from "@apollo/client";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Divider,
  Flex,
  IconButton,
  Text,
  NumberInput,
  Spinner,
  useToast,
  VStack,
  useColorMode,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { IAddress } from "../../services/graphql/Address";
import { DELETE_ADDRESS } from "../../services/graphql/mutation/Addresses";
import { ADDRESSES } from "../../services/graphql/query/Addresses";
import { useUnauthorizedAction } from "../../services/hooks/useUnauthorizedAction";
import { ContextApp } from "../../shared/ContextApp";
import { MessageError } from "../../shared/MessageError";
import { MessageInfo } from "../../shared/MessageInfo";
import { MessageSuccess } from "../../shared/MessageSuccess";
import { FormCreateAddress } from "./FormCreateAddress";

export const Address = () => {
  const { user, setUser } = useContext(ContextApp);
  const [loading, setLoading] = useState<boolean>(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const { action } = useUnauthorizedAction();
  const [addressToDelete, setAddressToDelete] = useState<number | undefined>();
  const [addressToUpdate, setAddressToUpdate] = useState<IAddress | undefined>();
  const { colorMode } = useColorMode();
  const [loadingOperateAddress, setLoadingOperateAddress] =
    useState<boolean>(false);
  const [deleteAddress] = useMutation(DELETE_ADDRESS);
  const [formCreateVisible, setFormCreateVisible] = useState(false);
  const [formUpdateVisible, setFormUpdateVisible] = useState(false);
  const toast = useToast();
  const cancelRef = useRef<any>();
  const { fetchMore } = useQuery(ADDRESSES);

  const unauthorized = useCallback(() => {
    action();
  }, []);

  const fetchAddresses = () => {
    setLoading(true);
    fetchMore({})
      .then((dataAddresses: any) => {
        const { data } = dataAddresses;
        const addresses = data;
        if (!addresses.addresses.length) {
          setFormCreateVisible(true);
          toast({
            duration: 3000,
            position: "top",
            render: () =>
              MessageInfo(
                "Usted no posee ninguna dirección registrada. Debe registrar alguna para continuar."
              ),
          });
        }
        setUser((oldState: any) => {
          const newState = JSON.parse(JSON.stringify(oldState));

          newState["address"] = addresses.addresses;

          return newState;
        });
        setAddresses(addresses.addresses);
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Error al recuperar las ciudades."
              ),
          });
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDeleteAddress = (id: number) => {
    setLoadingOperateAddress(true);
    deleteAddress({
      variables: {
        id: id,
      },
    })
      .then(() => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "Tu dirección se ha eliminado exitosamente!.",
            }),
        });
        fetchAddresses();
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                error.message
                  ? error.message
                  : "Ocurrió un error al eliminar su dirección. Por favor intente nuevamente."
              ),
          });
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setLoadingOperateAddress(false);
      });
  };

  useEffect(() => {
    fetchAddresses();
  }, []);
  const onCloseWithCleanState = () => {
    setAddressToDelete(undefined);
    onClose();
  };

  return (
    <>
      {loading ? (
        <Center>
          <Spinner />
        </Center>
      ) : addresses?.length && !formCreateVisible && !formUpdateVisible ? (
        <>
          <Flex justifyContent={{ sm: "flex-end" }}>
            <Button
              onClick={() => {
                setFormCreateVisible(true);
              }}
              colorScheme={"teal"}
              w={{ base: "100%", sm: "auto" }}
              float={{ sm: "right" }}
              mb={3}
            >
              <AddIcon mr={2} /> Agregar dirección
            </Button>
          </Flex>
          <Flex
            shadow={"inner"}
            direction={"column"}
            p={4}
            borderRadius="lg"
            borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
            borderWidth={"1px"}
            h="80vh"
            boxShadow={"inner"}
          >
            <VStack
              spacing={"5px"}
              direction={"row"}
              overflowY="auto"
              className="container-line-cart"
              p={2}
            >
              {addresses.map((address) => (
                <>
                  <Box
                    p={4}
                    display={"flex"}
                    flexDirection={{ base: "column", sm: "row" }}
                    alignItems={"center"}
                    minH={"200px"}
                    w="100%"
                    borderColor={
                      colorMode === "light" ? "gray.800" : "gray.100"
                    }
                    justifyContent="space-between"
                    h="auto"
                  >
                    <Flex
                      justifyContent={"center"}
                      direction={{ base: "column", sm: "row" }}
                      alignItems="center"
                    >
                      <Text my={5} fontWeight={"bold"}>
                        {address.street_name} {address.street_number}{" "}
                        {address.floor ? (
                          <Text fontWeight={"bold"}>
                            Piso:{" "}
                            <span style={{ fontWeight: "normal" }}>
                              {address.floor}
                            </span>
                          </Text>
                        ) : null}
                        {address.department ? (
                          <Text fontWeight={"bold"}>
                            Departamento:{" "}
                            <span style={{ fontWeight: "normal" }}>
                              {address.department}
                            </span>
                          </Text>
                        ) : null}
                      </Text>
                    </Flex>
                    <Box
                      display={"flex"}
                      alignItems="center"
                      justifyContent={"center"}
                      flexDirection={{ base: "column", sm: "row" }}
                    >
                      <IconButton
                        w={{ base: 100, sm: "auto" }}
                        mb={{ base: 3, sm: 0 }}
                        mr={{ base: 0, sm: 3 }}
                        icon={<EditIcon />}
                        aria-label="Editar dirección."
                        onClick={() => {
                          setFormUpdateVisible(true);
                          setAddressToUpdate(address);
                        }}
                      />
                      <IconButton
                        w={{ base: 100, sm: "auto" }}
                        icon={<DeleteIcon />}
                        aria-label="Eliminar dirección."
                        onClick={() => {
                          setAddressToDelete(address.id);
                          onOpen();
                        }}
                        isLoading={loadingOperateAddress}
                      />
                    </Box>
                  </Box>
                  <Divider />
                </>
              ))}
            </VStack>
          </Flex>
        </>
      ) : formCreateVisible ? (
        <Center>
          <Box minW="200px" maxW={"500px"} w="100%">
            <FormCreateAddress
              onSuccess={() => {
                fetchAddresses();
                setFormCreateVisible(false);
              }}
            />
          </Box>
        </Center>
      ) : (
        <Center>
          <Box minW="200px" maxW={"500px"} w="100%">
            <FormCreateAddress
              editMode={true}
              addressToUpdate={addressToUpdate}
              onSuccess={() => {
                fetchAddresses();
                setFormUpdateVisible(false);
              }}
            />
          </Box>
        </Center>
      )}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onCloseWithCleanState();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar dirección
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Seguro que desea eliminar la dirección?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onCloseWithCleanState();
                }}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (addressToDelete) {
                    onDeleteAddress(addressToDelete);
                  }
                  onCloseWithCleanState();
                }}
                ml={3}
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
